import React, { useState, useEffect } from 'react';
import { MapPin, Calculator, Clock, AlertCircle, CheckCircle, User, Info, Plus, Trash2, Home, Building, Briefcase, Sparkles } from 'lucide-react';
import useFrete, { Endereco } from '../hooks/useFrete';
import { useAuth } from '../contexts/AuthContext';
import { useMobileOptimizations } from '../hooks/useMobileOptimizations';
import toast from 'react-hot-toast';

interface EnderecoEntregaProps {
  onEnderecoChange: (endereco: Endereco | null) => void;
  onFreteChange: (valorFrete: number) => void;
  onAbrirPerfil?: () => void;
  entregaGratis?: boolean;
  valorFaltanteFreteGratis?: number;
  valorTotalItens?: number;
  tipoEntrega?: 'entrega' | 'retirada';
  onTipoEntregaChange?: (tipo: 'entrega' | 'retirada') => void;
  enderecoAtivo?: Endereco | null;
}

const EnderecoEntrega: React.FC<EnderecoEntregaProps> = ({
  onEnderecoChange,
  onFreteChange,
  onAbrirPerfil,
  entregaGratis = false,
  valorFaltanteFreteGratis = 0,
  valorTotalItens = 0,
  tipoEntrega = 'entrega',
  onTipoEntregaChange,
  enderecoAtivo
}) => {
  const { usuario, adicionarEndereco, excluirEndereco, listarEnderecos } = useAuth();
  const { isMobile } = useMobileOptimizations();

  const [modoAdicionar, setModoAdicionar] = useState(false);
  const [novoEndereco, setNovoEndereco] = useState<Endereco>({
    rua: '',
    numero_casa: '',
    bairro: '',
    cidade: '',
    estado: 'SC',
    cep: ''
  });
  const [isCepLoading, setIsCepLoading] = useState(false);
  const [enderecoSelecionadoId, setEnderecoSelecionadoId] = useState<number | null>(null);

  const empresaId = usuario?.empresa_id || 1;
  const { calcularFrete, resultadoFrete, loading, error, limparFrete, infoFrete } = useFrete({ empresaId });
  const ultimoEnderecoCalculadoRef = React.useRef<string>('');

  // Carregar endereços ao montar
  useEffect(() => {
    if (usuario) {
      listarEnderecos();
    }
  }, [usuario?.id]);

  // Sincronizar endereço selecionado
  useEffect(() => {
    if (tipoEntrega === 'entrega' && usuario?.enderecos && usuario.enderecos.length > 0) {
      // Prioridade 1: Sincronizar com o endereço ativo vindo do pai (se houver ID)
      if (enderecoAtivo?.id) {
        setEnderecoSelecionadoId(enderecoAtivo.id);
        const endKey = `${enderecoAtivo.rua}_${enderecoAtivo.numero_casa}_${enderecoAtivo.cep}_${empresaId}`;
        // Se o endereço ativo tem ID mas não foi calculado, calcular
        if (!resultadoFrete && ultimoEnderecoCalculadoRef.current !== endKey) {
          ultimoEnderecoCalculadoRef.current = endKey;
          calcularFrete(enderecoAtivo);
        }
      }
      // Prioridade 2: Auto-seleção inicial (apenas se nem o pai nem o filho tiverem seleção)
      else if (enderecoSelecionadoId === null && !enderecoAtivo) {
        const principal = usuario.enderecos[0];
        handleSelecionarEndereco(principal);
      }
    } else if (tipoEntrega === 'retirada') {
      onEnderecoChange(null);
      onFreteChange(0);
      setEnderecoSelecionadoId(null);
      limparFrete();
    }
  }, [usuario?.enderecos, tipoEntrega, enderecoAtivo?.id]);

  // Aplicar frete quando calculado
  useEffect(() => {
    if (resultadoFrete && tipoEntrega === 'entrega') {
      if (!entregaGratis) {
        onFreteChange(resultadoFrete.valorFrete);
      } else {
        onFreteChange(0);
      }
    }
  }, [resultadoFrete, entregaGratis, tipoEntrega]);

  const formatCurrency = (value: any): string => {
    const numValue = Number(value);
    if (isNaN(numValue)) return '0.00';
    return numValue.toFixed(2);
  };

  const formatarCEP = (valor: string) => {
    const numeros = valor.replace(/\D/g, '');
    if (numeros.length <= 5) return numeros;
    return `${numeros.slice(0, 5)}-${numeros.slice(5, 8)}`;
  };

  const buscarCEP = async (cep: string) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;

    setIsCepLoading(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await res.json();

      if (!data.erro) {
        setNovoEndereco(prev => ({
          ...prev,
          rua: data.logradouro || '',
          bairro: data.bairro || '',
          cidade: data.localidade || '',
          estado: data.uf || '',
          cep: formatarCEP(cepLimpo)
        }));
      }
    } catch {
      // Ignorar erro de CEP silenciosamente
    } finally {
      setIsCepLoading(false);
    }
  };

  const handleSelecionarEndereco = (end: any) => {
    setEnderecoSelecionadoId(end.id);
    const enderecoFormatado: Endereco = {
      rua: end.rua,
      numero_casa: end.numero_casa,
      bairro: end.bairro,
      cidade: end.cidade,
      estado: end.estado || 'SC',
      cep: end.cep
    };
    onEnderecoChange(enderecoFormatado);
    const endKey = `${end.rua}_${end.numero_casa}_${end.cep}_${empresaId}`;
    if (ultimoEnderecoCalculadoRef.current !== endKey && end.rua && end.numero_casa && end.bairro && end.cep) {
      ultimoEnderecoCalculadoRef.current = endKey;
      calcularFrete(enderecoFormatado, empresaId);
    }
  };

  const handleAdicionarEndereco = async () => {
    if (!novoEndereco.rua || !novoEndereco.numero_casa || !novoEndereco.bairro || !novoEndereco.cep) {
      toast.error('Preencha todos os campos do endereço');
      return;
    }

    try {
      await adicionarEndereco(novoEndereco);
      toast.success('Endereço adicionado com sucesso!');
      setModoAdicionar(false);
      setNovoEndereco({ rua: '', numero_casa: '', bairro: '', cidade: '', estado: 'SC', cep: '' });
      listarEnderecos();
    } catch (err: any) {
      toast.error(err.message || 'Erro ao adicionar endereço');
    }
  };

  const handleExcluirEndereco = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Tem certeza que deseja excluir este endereço?')) return;

    try {
      await excluirEndereco(id);
      toast.success('Endereço excluído!');
      if (enderecoSelecionadoId === id) {
        setEnderecoSelecionadoId(null);
        onEnderecoChange(null);
        onFreteChange(0);
        limparFrete();
      }
    } catch (err) {
      toast.error('Erro ao excluir endereço');
    }
  };

  if (!usuario) {
    return (
      <div className="bg-gray-800/40 backdrop-blur-sm rounded-2xl p-6 border border-white/5 text-center">
        <User className="h-10 w-10 text-gray-500 mx-auto mb-3" />
        <p className="text-gray-300 font-bold">Faça login para gerenciar seus endereços</p>
        <button onClick={onAbrirPerfil} className="mt-4 text-orange-400 font-bold hover:underline">Entrar / Cadastrar</button>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${isMobile ? 'p-2' : 'p-0'}`}>
      {/* Toggle Entrega / Retirada */}
      <div className="flex bg-gray-900/60 p-1 rounded-2xl border border-white/5">
        <button
          onClick={() => onTipoEntregaChange?.('entrega')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl transition-all duration-300 ${tipoEntrega === 'entrega' ? 'bg-gradient-to-r from-[#FF5B00] via-[#FF6D1F] to-[#FFA000] text-white shadow-lg shadow-[#FF5B00]/25 font-bold' : 'text-gray-400 hover:text-white font-medium'}`}
        >
          <MapPin size={18} />
          <span className="font-bold text-sm">Entrega</span>
        </button>
        <button
          onClick={() => onTipoEntregaChange?.('retirada')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl transition-all duration-300 ${tipoEntrega === 'retirada' ? 'bg-gradient-to-r from-[#FF5B00] via-[#FF6D1F] to-[#FFA000] text-white shadow-lg shadow-[#FF5B00]/25 font-bold' : 'text-gray-400 hover:text-white font-medium'}`}
        >
          <Home size={18} />
          <span className="font-bold text-sm">Retirar</span>
        </button>
      </div>

      {tipoEntrega === 'entrega' ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-white font-bold flex items-center gap-2">
              <MapPin className="text-orange-500 h-5 w-5" />
              Meus Endereços
            </h3>
            {(!usuario.enderecos || usuario.enderecos.length < 3) && !modoAdicionar && (
              <button
                onClick={() => setModoAdicionar(true)}
                className="text-[10px] uppercase tracking-widest font-bold text-orange-400 hover:text-orange-300 flex items-center gap-1"
              >
                <Plus size={14} /> Novo
              </button>
            )}
          </div>

          {modoAdicionar ? (
            <div className="bg-gray-800/40 backdrop-blur-sm rounded-2xl p-4 border border-orange-500/20 animate-fade-in space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-[10px] text-gray-500 font-bold uppercase ml-1">CEP</label>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="00000-000"
                      value={novoEndereco.cep}
                      onChange={(e) => {
                        const val = formatarCEP(e.target.value);
                        setNovoEndereco(prev => ({ ...prev, cep: val }));
                        if (val.replace(/\D/g, '').length === 8) buscarCEP(val);
                      }}
                      className="w-full bg-gray-900/60 border border-white/5 rounded-xl px-4 py-2.5 text-white text-sm focus:ring-2 focus:ring-orange-500/50 outline-none"
                    />
                    {isCepLoading && <div className="absolute right-3 top-2.5 animate-spin"><Calculator size={16} className="text-orange-500" /></div>}
                  </div>
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] text-gray-500 font-bold uppercase ml-1">Rua</label>
                  <input
                    type="text"
                    placeholder="Nome da rua"
                    value={novoEndereco.rua}
                    onChange={(e) => setNovoEndereco(prev => ({ ...prev, rua: e.target.value }))}
                    className="w-full bg-gray-900/60 border border-white/5 rounded-xl px-4 py-2.5 text-white text-sm focus:ring-2 focus:ring-orange-500/50 outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 font-bold uppercase ml-1">Número</label>
                  <input
                    type="text"
                    placeholder="Nº"
                    value={novoEndereco.numero_casa}
                    onChange={(e) => setNovoEndereco(prev => ({ ...prev, numero_casa: e.target.value }))}
                    className="w-full bg-gray-900/60 border border-white/5 rounded-xl px-4 py-2.5 text-white text-sm focus:ring-2 focus:ring-orange-500/50 outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-gray-500 font-bold uppercase ml-1">Bairro</label>
                  <input
                    type="text"
                    placeholder="Bairro"
                    value={novoEndereco.bairro}
                    onChange={(e) => setNovoEndereco(prev => ({ ...prev, bairro: e.target.value }))}
                    className="w-full bg-gray-900/60 border border-white/5 rounded-xl px-4 py-2.5 text-white text-sm focus:ring-2 focus:ring-orange-500/50 outline-none"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  onClick={() => setModoAdicionar(false)}
                  className="flex-1 bg-white/5 hover:bg-white/10 text-gray-400 font-bold py-2.5 rounded-xl text-xs uppercase"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAdicionarEndereco}
                  className="flex-1 bg-gradient-to-r from-[#FF5B00] via-[#FF6D1F] to-[#FFA000] hover:from-[#E04F00] hover:to-[#E69000] text-white font-bold py-2.5 rounded-xl text-xs uppercase shadow-lg shadow-[#FF5B00]/25 transition-all duration-300"
                >
                  Salvar
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {usuario.enderecos?.map((end: any, idx: number) => (
                <div
                  key={end.id}
                  onClick={() => handleSelecionarEndereco(end)}
                  className={`relative p-4 rounded-2xl border transition-all cursor-pointer group ${enderecoSelecionadoId === end.id ? 'bg-[#FF5B00]/10 border-[#FF5B00] shadow-lg shadow-[#FF5B00]/10' : 'bg-gray-800/40 border-white/5 hover:border-white/10'}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-xl ${enderecoSelecionadoId === end.id ? 'bg-[#FF5B00]' : 'bg-white/5'}`}>
                      {idx === 0 ? <Home size={16} /> : idx === 1 ? <Briefcase size={16} /> : <Building size={16} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-bold text-sm truncate">{end.rua}, {end.numero_casa}</p>
                      <p className="text-gray-500 text-xs truncate">{end.bairro} • {end.cep}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <button
                        onClick={(e) => handleExcluirEndereco(end.id, e)}
                        className="p-1.5 text-gray-600 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                      >
                        <Trash2 size={14} />
                      </button>
                      {enderecoSelecionadoId === end.id && <CheckCircle size={16} className="text-[#FF5B00]" />}
                    </div>
                  </div>
                </div>
              ))}

              {(!usuario.enderecos || usuario.enderecos.length === 0) && (
                <div className="py-10 text-center bg-gray-800/20 rounded-2xl border border-dashed border-white/10">
                  <p className="text-gray-500 text-xs uppercase font-black tracking-widest">Nenhum endereço salvo</p>
                  <button onClick={() => setModoAdicionar(true)} className="mt-4 bg-white/5 hover:bg-white/10 px-6 py-2 rounded-xl text-[10px] font-bold text-white uppercase tracking-widest transition-all">
                    Cadastrar Primeiro
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Frete e Info */}
          {resultadoFrete && !modoAdicionar && (
            <div className="bg-gradient-to-r from-[#FF5B00]/15 via-[#FFA000]/10 to-[#FF5B00]/5 rounded-2xl p-4 border border-[#FF5B00]/25 animate-fade-in flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-[#FF5B00]/20 rounded-xl">
                  <Clock size={16} className="text-[#FF7A40]" />
                </div>
                <div>
                  <p className="text-[10px] text-[#FFA000] uppercase font-bold tracking-widest">Entrega estimada</p>
                  <p className="text-white font-bold text-sm">{resultadoFrete.tempoEstimado}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-gray-400 uppercase font-bold tracking-widest">Taxa</p>
                <p className={`text-lg font-black ${entregaGratis ? 'text-[#00B368]' : 'text-white'}`}>
                  {entregaGratis ? 'GRÁTIS' : `R$ ${formatCurrency(resultadoFrete.valorFrete)}`}
                </p>
              </div>
            </div>
          )}

          {error && !loading && !modoAdicionar && (
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center gap-3">
              <AlertCircle size={20} className="text-red-500 flex-shrink-0" />
              <p className="text-red-200 text-xs font-bold leading-tight">{error}</p>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-gray-800/40 backdrop-blur-sm rounded-2xl p-6 border border-white/5 text-center animate-fade-in">
          <div className="p-4 bg-orange-500/10 rounded-full w-fit mx-auto mb-4 border border-orange-500/20">
            <Building className="h-8 w-8 text-orange-500" />
          </div>
          <h3 className="text-white font-bold mb-2 uppercase tracking-widest text-sm">Retirar no Balcão</h3>
          <p className="text-gray-400 text-xs leading-relaxed max-w-[240px] mx-auto mb-4">
            Seu pedido estará pronto em aproximadamente 25-40 minutos no nosso endereço físico.
          </p>
          <div className="bg-gray-900/60 p-3 rounded-xl border border-white/5">
            <p className="text-[10px] text-gray-500 font-bold uppercase mb-1">Taxa de entrega</p>
            <p className="text-green-500 font-black text-lg">ECONOMIZE R$ 0,00</p>
          </div>
        </div>
      )}

      {/* Frete Grátis Progress */}
      {valorFaltanteFreteGratis > 0 && tipoEntrega === 'entrega' && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-2xl p-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-[10px] text-yellow-500 font-bold uppercase tracking-widest flex items-center gap-1">
              <Sparkles size={12} /> Desbloqueie Frete Grátis
            </span>
            <span className="text-[10px] text-yellow-500 font-bold">R$ {formatCurrency(valorFaltanteFreteGratis)} faltantes</span>
          </div>
          <div className="h-1.5 bg-gray-700/50 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-yellow-500 to-orange-500 transition-all duration-500"
              style={{ width: `${Math.min(100, (valorTotalItens / (valorTotalItens + valorFaltanteFreteGratis)) * 100)}%` }}
            ></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnderecoEntrega;
